import type { ApolloClient } from '@apollo/client';
import { CarbonWebsiteV1DesignPurpose } from '@graphql/generated-base';
import type {
  WebsiteV2CreateSiteWithV1DesignMutation,
  WebsiteV2CreateSiteWithV1DesignMutationVariables,
  WebsiteV2SwitchTemplatesQuery,
  WebsiteV2SwitchTemplatesQueryVariables,
} from './websiteV2SiteFromV1Design.graphql.generated';
import { WebsiteV2CreateSiteWithV1DesignDocument, WebsiteV2SwitchTemplatesDocument } from './websiteV2SiteFromV1Design.graphql.generated';

const WEDDING_WEBSITE_KIND = 'weddingWebsite';
/** The V1-parity template. Ids differ per environment, so it is picked by folder, never by id. */
const BRANNAN_TEMPLATE_FOLDER = 'brannan';

export type CreateWebsiteV2SiteFromV1DesignResult = Readonly<{ status: 'created'; siteId: string }> | Readonly<{ status: 'noTemplate' }>;

/**
 * Creates the event's V2 site from the Brannan template with its live V1 design carried in
 * (template mode is a snapshot copy — seconds, no LLM). Shared by the dashboard switch and the
 * createwedding rollout, which adapts every enrolled event's starting V1 design. Event admins may
 * call it (carbon grants them `create Site` on their event).
 *
 * Resolves `noTemplate` when the environment has no published Brannan template; throws on any
 * request failure. The caller owns reporting.
 */
export const createWebsiteV2SiteFromV1Design = async (client: ApolloClient<unknown>, eventId: string): Promise<CreateWebsiteV2SiteFromV1DesignResult> => {
  const templates = await client.query<WebsiteV2SwitchTemplatesQuery, WebsiteV2SwitchTemplatesQueryVariables>({
    query: WebsiteV2SwitchTemplatesDocument,
    variables: { kind: WEDDING_WEBSITE_KIND },
    fetchPolicy: 'network-only',
  });
  const brannan = templates.data.carbonWebsiteTemplates.find(template => template.published && template.s3Folder === BRANNAN_TEMPLATE_FOLDER);
  if (!brannan) return { status: 'noTemplate' };

  const created = await client.mutate<WebsiteV2CreateSiteWithV1DesignMutation, WebsiteV2CreateSiteWithV1DesignMutationVariables>({
    mutation: WebsiteV2CreateSiteWithV1DesignDocument,
    variables: { input: { eventId, templateId: brannan.id, v1Design: { fromEvent: { purpose: CarbonWebsiteV1DesignPurpose.live } } } },
  });
  const siteId = created.data?.carbonWeddingWebsiteGenerateFromEvent.site?.id;
  if (!siteId) throw new Error('Missing created site');
  return { status: 'created', siteId };
};
